import { Component, EventEmitter, Input, OnChanges, OnDestroy, Output, SimpleChanges } from '@angular/core';
import { AuthService } from '../../core/security/auth.service';
import { Router } from '@angular/router';
import { MessagingGroupModel } from '../../api/models/messaging-group-model';
import { ContactLookupModel } from '../../api/models';
import { HeaderService } from '../../core/services/header.service';
import { ProMessagingService } from '../pro-messaging.service';
import { DateTime } from 'luxon';
import { HumanizedDurationPipe } from '../../core/pipes/humanized-duration.pipe';
import { PhoneNumbersUtils } from '../../core/utilities/phone-numbers-helpers';
import { NgIf } from '@angular/common';
import { FlexModule } from '@angular/flex-layout';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { MatMiniFabAnchor, MatIconButton, MatIconAnchor } from '@angular/material/button';
import { MatIcon } from '@angular/material/icon';
import { MatTooltip } from '@angular/material/tooltip';
import { MatBadge } from '@angular/material/badge';
import { PhoneNumberPipe } from '../../core/pipes/phone-number.pipe';
import { HighlightPipe } from '../../core/pipes/highlight.pipe';
import { HumanizedDatePipe } from '../../core/pipes/humanized-date.pipe';
import { InitialsPipe } from '../../core/pipes/initials.pipe';
import { NameInitialsPipe } from '../../core/pipes/name-initials.pipe';

@Component({
    selector: 'app-pro-phone-number-group-item-v2',
    template: `
    <div class="item-container" fxLayout="row" fxLayoutAlign="start center" *ngIf="!dataItem">
      <div class="item-avatar">
        <mat-spinner diameter="32"></mat-spinner>
      </div>
      <div class="item-content">
        <div class="item-header">
          <h4 class="item-heading">Loading...</h4>
        </div>
      </div>
    </div>
    <div class="item-container" fxLayout="row" fxLayoutAlign="start stretch" *ngIf="dataItem">
      <div class="item-avatar" fxLayout="column" fxLayoutAlign="start center">
        <a mat-mini-fab class="button-32 mat-elevation-z0 unknown-user" *ngIf="!primaryContact?.name">
          <mat-icon>perm_identity</mat-icon>
        </a>
        <a mat-mini-fab class="button-32 mat-elevation-z0 unknown-user" *ngIf="primaryContact?.name">
          <span>{{ primaryContact.name | initials }}</span>
        </a>
        <button mat-icon-button class="button-32 pin-conversation-btn" (click)="onPinClicked(dataItem)">
          <mat-icon
            fontSet="material-icons-outlined"
            *ngIf="!dataItem.pin && !(pinUpdateInProgress.inProgress && pinUpdateInProgress.threadKey === dataItem.threadKey)"
            matTooltip="Pin conversation in top of the list"
          >
            push_pin
          </mat-icon>
          <mat-icon fontSet="material-icons" color="primary"
                    *ngIf="dataItem.pin && !(pinUpdateInProgress.inProgress && pinUpdateInProgress.threadKey === dataItem.threadKey)"
                    matTooltip="Unpin conversation"
          >push_pin
          </mat-icon>
          <mat-spinner *ngIf="pinUpdateInProgress.inProgress && pinUpdateInProgress.threadKey === dataItem.threadKey" mode="indeterminate"
                       diameter="24" strokeWidth="2"></mat-spinner>
        </button>
      </div>
      <div class="item-content" fxFlex fxLayout="column" fxLayoutAlign="space-between stretch">
        <div class="item-header">
          <h4 class="item-heading">
            <span class="item-contact">
              {{ primaryContact?.name || primaryContact?.phoneNumber | phoneNumber }}
              <a
                mat-icon-button
                class="button-32"
                matTooltip="Add to contacts"
                *ngIf="hasContactsModule && hasContactsEditRight && primaryContact?.id === '0'"
                (click)="addNewContact($event, primaryContact)"
              >
                <mat-icon fontSet="material-icons-outlined">person_add</mat-icon>
              </a>
            </span>
            <span class="item-phone" *ngIf="primaryContact?.name">{{ primaryContact?.phoneNumber | phoneNumber }}</span>
          </h4>
        </div>
        <div class="item-last-message" fxLayout="row" fxLayoutAlign="start center">
          <p [innerHTML]="dataItem.lastMessage || 'This message was deleted' | highlight: searchText"></p>
        </div>
      </div>
      <div class="right-holder" fxLayout="column" fxLayoutAlign="start end">
        <time class="item-time">
          {{ dataItem.lastTime | humanizedDate }}
        </time>
        <time class="item-time" *ngIf="dataItem.wabaConvId">
          {{ expiresIn }}
        </time>
        <div *ngIf="!!dataItem.wabaConvId">
          <i
            class="fa-brands fa-whatsapp messaging-type-waba"
            style="font-size: 24px"
            matTooltip="WhatsApp conversation"
          ></i>
        </div>
        <div *ngIf="!!dataItem.isBroadcast">
          <i class="material-icons-outlined" matTooltip="Broadcast conversation">group</i>
        </div>
        <div *ngIf="dataItem.isGroup">
          <i class="material-icons" matTooltip="Group conversation">group</i>
        </div>
        <div
          class="item-badge"
          *ngIf="dataItem.unread > 0"
          [matBadge]="dataItem.unread"
          matBadgePosition="before"
          matBadgeColor="warn"
        ></div>
      </div>
    </div>
  `,
    styleUrls: ['./pro-phone-number-group-item-v2.component.scss'],
    standalone: true,
    imports: [
        NgIf,
        FlexModule,
        MatProgressSpinner,
        MatMiniFabAnchor,
        MatIcon,
        MatIconButton,
        MatTooltip,
        MatIconAnchor,
        MatBadge,
        PhoneNumberPipe,
        HighlightPipe,
        HumanizedDatePipe,
        InitialsPipe,
        NameInitialsPipe,
    ],
})
export class ProPhoneNumberGroupItemV2Component implements OnDestroy, OnChanges {
  @Input() dataItem: MessagingGroupModel;
  @Input() wabaConvExp: string;
  @Input() searchText: string;
  @Input() hasContactsModule = false;
  @Input() hasContactsEditRight = false;
  @Input() pinUpdateInProgress = { threadKey: '', inProgress: false };
  @Output() pinChangedEvent = new EventEmitter<MessagingGroupModel>();

  primaryContact: ContactLookupModel;

  humanizedDurationPipe = new HumanizedDurationPipe();
  expiresIn = '';
  expiresInTimeout: any;

  constructor(
    public authService: AuthService,
    public headerService: HeaderService,
    public messagingService: ProMessagingService,
    private router: Router,
  ) {}

  ngOnChanges(changes: SimpleChanges): void {
    // console.log('group item changed', this.dataItem);
    if (!!this.dataItem && this.dataItem.contacts && this.dataItem.contacts.length > 0) {
      this.primaryContact = this.dataItem.contacts[0];
    }

    if (this.dataItem && this.dataItem.rowNumber === -99) {
      this.updateContactInformationFromServer();
    }

    this.updateConversationExpiration();
  }

  updateConversationExpiration() {
    this.expiresIn = 'Expired';
    if (this.dataItem) {
      if (this.dataItem.wabaConvExp) {
        const expDate = DateTime.fromISO(this.dataItem.wabaConvExp);
        const now = DateTime.local();
        if (expDate > now) {
          this.expiresIn = `Expires in ${this.humanizedDurationPipe.transform(this.dataItem.wabaConvExp)}`;

          if (this.expiresInTimeout) {
            clearTimeout(this.expiresInTimeout);
          }
          this.expiresInTimeout = setTimeout(() => {
            this.updateConversationExpiration();
          }, 30000);
        }
      }
    }
  }

  ngOnDestroy() {
    clearTimeout(this.expiresInTimeout);
  }

  addNewContact($event: MouseEvent, primaryContact: ContactLookupModel) {
    $event.preventDefault();
    this.router.navigate([this.headerService.themePrefix, 'contacts', 'contact', 'new', 'edit'], {
      queryParams: { phoneNumber: primaryContact?.phoneNumber },
    });
  }

  async onPinClicked(dataItem: MessagingGroupModel) {
    this.pinChangedEvent.emit(dataItem);
  }

  private updateContactInformationFromServer() {
    console.log('updateContactInformationFromServer');
    for (const contact of this.dataItem.contacts) {
      const phoneNumberNational = PhoneNumbersUtils.phoneNumberToNational(contact.phoneNumber);
      this.messagingService.getContactsAndTagsObservable(phoneNumberNational).subscribe(contacts => {
        if (contacts.length > 0) {
          const contactInfo = contacts[0];
          contact.phoneNumber = contactInfo.phoneNumber;
          contact.name = contactInfo.name;
          contact.id = contactInfo.contactId;
          contact.phoneType = 'Contacts';
          contact.communicationType = contactInfo.communicationType;
          contact.isOptedIn = contactInfo.isOptedIn;
        }
      });
    }
  }
}
